import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { APP_CONSTANTS } from '@/app/config/constants';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { entitiesReported } from '@/app/store/selectedEntitySlice';
import { FirstLoginPasswordDialog, selectUser, useSessionWatch } from '@/features/authentication';
import { ImportProgressProvider } from '@/features/masters/common/import-export/components/ImportProgressContext';
import { ImportProgressTray } from '@/features/masters/common/import-export/components/ImportProgressTray';
import '../layout.css';
import { useSidebar } from '../hooks/useSidebar';
import { getPageTitle } from '../utils/pageTitle';
import { Header } from './header/Header';
import { Sidebar } from './sidebar/Sidebar';
import { useEntityDisplayFormat } from '../hooks/useEntityDisplayFormat';

export function Layout() {
  const { isCollapsed, toggleSidebar } = useSidebar();
  /*
    The selected entity's date and time format, read once per entity and kept in the store where every
    renderer can find it. Here rather than per screen: it belongs to the session's entity, not to any one
    page, and loading it per page would re-read it on each navigation.
  */
  useEntityDisplayFormat();
  /* A user deleted while signed in is returned to the sign-in page within a minute (2026-10-13). */
  useSessionWatch();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();

  const entities = useAppSelector(selectUser)?.entityDetail;
  const selectedEntityId = useAppSelector((state) => state.selectedEntity.id);

  useEffect(() => {
    document.title = `${getPageTitle(pathname)} · ${APP_CONSTANTS.APP_NAME}`;
  }, [pathname]);

  /*
    Reconcile the remembered entity with the ones this user actually holds.

    Here rather than in the auth saga because it has to run for a restored session too, not only for a
    fresh sign-in: the shell mounts in both cases, and a reload that skipped this would send yesterday's
    entity in the header. The reducer keeps the remembered choice when it is still assigned and falls back
    to the first when it is not.
  */
  useEffect(() => {
    dispatch(entitiesReported(entities));
  }, [dispatch, entities]);

  return (
    /*
      The import tray's provider wraps the whole shell, so a queued import outlives both the dialog that
      started it and the screen it was started from. Inside the <main> it would be remounted by the
      entity key below, which is exactly the navigation it has to survive.
    */
    <ImportProgressProvider>
    <div className="app-layout__root">
      {/* The sidebar puts ~11 tab stops ahead of the content. */}
      <a className="app-layout__skip-link" href="#main-content">
        Skip to main content
      </a>

      <Sidebar isCollapsed={isCollapsed} />

      <div className="app-layout__main-col">
        <Header isCollapsed={isCollapsed} onToggleSidebar={toggleSidebar} />
        <main className="app-layout__main" id="main-content">
          {/*
            Keyed by the entity, so changing it remounts the screen underneath.

            The header alone would leave the current screen showing the previous entity's rows until
            something happened to refetch them -- every list holds what it already loaded. Remounting is
            the one change that reaches every screen at once, including those with no notion of a scope,
            rather than each of them growing an effect that watches the selection.
          */}
          <Outlet key={selectedEntityId ?? 'no-entity'} />
        </main>
      </div>

      {/* Fixed to the bottom-right of the shell, over whatever screen is showing. */}
      <ImportProgressTray />

      {/*
        A user still on their first password is held here, over whichever page they landed on, until they
        change it. Renders nothing for everybody else.
      */}
      <FirstLoginPasswordDialog />
    </div>
    </ImportProgressProvider>
  );
}
